import React, { useState, useEffect, useCallback } from 'react';
import type { ReactNode, ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import type { Resource } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, invalidateDirectory, useChurchConfig, usePrint, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { resolveTemplate, buildPrintHtml, NoTemplateError } from '../../utils/printService';
import { getPreferences } from '../../utils/preferences';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, Pagination, Notification, PromptDialog, ConfirmDialog, FormLayout, Button, Badge, RowActions, TablePanel, DateRangeFilter, PrintButton
} from '../../components/ui';
import type { Column, BadgeTone } from '../../components/ui';
import type { DocumentType } from '../../types';
import { stripFormLocal } from './formLocal';

// One shell for a merged register (Marriage = banns + certificates, Baptism =
// baptism + confirmation). The list is server-paged across both tables via a
// UNION; the full record is fetched by id when a row is opened, edited, printed
// or deleted. Each type supplies its own form and printable certificate.

export interface MergedType {
  /** Matches the backend `type` value, e.g. 'BANN'. */
  key: string;
  tabLabel: string;      // '📜 Banns'
  shortLabel: string;    // 'Banns' (the row badge)
  tone: BadgeTone;
  countKey: string;      // key in the /counts response
  recordLabel: string;   // 'Banns entry'
  resource: Resource<any>;
  emptyForm: Record<string, unknown>;
  toForm: (full: any) => Record<string, unknown>;
  /** Refuses the save with a message, or returns null when valid. */
  validate?: (form: any) => string | null;
  /**
   * Raises a question the clerk answers, rather than a rule they cannot pass:
   * confirming someone with no baptism on file is ordinary for a member who
   * transferred in, so it asks instead of blocking.
   */
  warn?: (form: any, isEditing: boolean) => string | null;
  fields: (form: any, setForm: (f: any) => void, ctx: MergedFieldContext) => ReactNode;
  renderDocument?: (full: any) => ReactNode;
  /**
   * What this record is, for printing. The module and the kind of document
   * name the template the parish configured; `printData` maps the saved row
   * onto the placeholders that template was written against.
   */
  printModule?: string;
  printDocumentType?: DocumentType;
  printData?: (full: any) => Record<string, unknown>;
  /**
   * The register itself as a document, separate from the certificate for one
   * row: a parish prints "the baptism register for 2026" as well as a single
   * certificate, and the two are laid out nothing alike.
   */
  listDocumentType?: DocumentType;
}

/** Passed to a register's `fields`, for the controls that need theming. */
export interface MergedFieldContext {
  isDarkMode?: boolean;
  /** True while an existing entry is open, not a new one being filed. */
  isEditing: boolean;
}

export interface MergedRegisterConfig {
  icon: string;
  title: string;
  pageApi: (params?: any, options?: any) => Promise<any>;
  countsApi: (options?: any) => Promise<Record<string, unknown>>;
  /** The middle columns (date + type-specific). Record No. and Type are added. */
  columns: Array<Column<any>>;
  searchPlaceholder: string;
  types: MergedType[];
  /**
   * The register's Summary tab: figures across every live entry, not one page.
   * When given it is the first tab and the one the screen opens on.
   */
  summary?: ComponentType<{ isDarkMode?: boolean }>;
}

const stamp = () => new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const MergedRegisterScreen = ({ config, isDarkMode }: { config: MergedRegisterConfig; isDarkMode?: boolean }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const { notification, notify, dismiss } = useNotification();
  const { print } = usePrint(notify);

  const typeKeys = config.types.map((x) => x.key);
  const typeConf = (key: string) => config.types.find((x) => x.key === key) || config.types[0];

  const [activeTab, setActiveTab] = useState(config.summary ? 'summary' : 'all');
  const [formType, setFormType] = useState(config.types[0].key);
  const [form, setForm] = useState<any>(config.types[0].emptyForm);
  const [editing, setEditing] = useState<{ id: number; type: string; full: any } | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ full: any; type: string } | null>(null);
  const [pendingVoid, setPendingVoid] = useState<any>(null);
  const [pendingWarning, setPendingWarning] = useState<string | null>(null);

  // The Sacraments list links straight here: `form` opens a new entry with the
  // member already chosen, `search` finds the entry they already have. Read
  // once on arrival — re-applying it would fight the clerk on every render.
  const seed = (useLocation().state as any)?.register;
  useEffect(() => {
    if (!seed) return;
    const key = typeKeys.includes(seed.type) ? seed.type : config.types[0].key;
    if (seed.form) {
      setFormType(key);
      setForm({ ...typeConf(key).emptyForm, ...seed.form });
      setActiveTab('add');
    } else if (seed.search) {
      setActiveTab(typeKeys.includes(seed.type) ? seed.type : 'all');
      setSearchTerm(seed.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);
  // From / to on the sacrament date, applied on the server.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const clearRange = () => { setFromDate(''); setToDate(''); };

  const isDeletedView = activeTab === 'deleted';
  const isListView = activeTab === 'all' || isDeletedView || typeKeys.includes(activeTab);
  const typeParam = typeKeys.includes(activeTab) ? activeTab : '';
  const bucketParam = isDeletedView ? 'deleted' : 'active';

  useEffect(() => { setPageNum(1); }, [activeTab, debouncedSearch, fromDate, toDate]);

  /**
   * The certificate exactly as it will print.
   *
   * The screen used to draw its own React version of the document while the
   * printer used the parish's template — two different documents behind one
   * button. It also inherited the application's colours, so in dark mode the
   * certificate came out as pale text with invisible black signature rules.
   * Both problems go away by showing the real thing: the same standalone HTML
   * the printer is handed, in its own frame.
   */
  const churchConfig = useChurchConfig();
  const [paper, setPaper] = useState<{ html: string; error: string } | null>(null);

  useEffect(() => {
    if (!preview) { setPaper(null); return undefined; }
    const conf = typeConf(preview.type);
    if (!conf.printModule || !conf.printDocumentType) {
      setPaper({ html: '', error: 'No document is configured for this record.' });
      return undefined;
    }

    let live = true;
    const request = {
      module: conf.printModule,
      documentType: conf.printDocumentType,
      data: conf.printData ? conf.printData(preview.full) : preview.full,
      config: churchConfig,
      title: `${conf.recordLabel} ${preview.full?.recordId || ''}`.trim()
    };

    resolveTemplate(request)
      .then((template) => {
        if (!live) return;
        setPaper({ html: buildPrintHtml(template, request.data, churchConfig, request.title), error: '' });
      })
      .catch((err) => {
        if (!live) return;
        setPaper({
          html: '',
          error: err instanceof NoTemplateError
            ? err.message
            : (err as Error)?.message || 'The document could not be prepared.'
        });
      });

    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, churchConfig]);

  /**
   * The register this tab is showing, as a printable document. Empty on the
   * "All" and "Deleted" tabs, where the rows are of mixed kinds.
   */
  const listPrint = (() => {
    if (!typeParam) return null;
    const ty = typeConf(activeTab);
    if (!ty.printModule || !ty.listDocumentType) return null;
    return { module: ty.printModule, documentType: ty.listDocumentType, label: ty.shortLabel || config.title };
  })();

  const { data: rows, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<any>((p, o) => config.pageApi(p, o), {
      params: { type: typeParam, bucket: bucketParam, q: debouncedSearch, from: fromDate, to: toDate, page: pageNum - 1, size: PAGE_SIZE },
      enabled: isListView
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const [counts, setCounts] = useState<Record<string, number>>({});
  const refetchCounts = useCallback(async () => {
    try {
      const c = await config.countsApi();
      const out: Record<string, number> = {};
      Object.keys(c).forEach((k) => { out[k] = Number(c[k]) || 0; });
      setCounts(out);
    } catch { /* keep last counts */ }
  }, [config]);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts()]);
  }, [refetchPage, refetchCounts]);

  const page = {
    currentItems: rows, currentPage: pageNum, totalPages, totalItems, itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1, isLast: pageNum >= totalPages, reset: () => setPageNum(1)
  };

  const resetForm = () => {
    setEditing(null);
    setFormType(config.types[0].key);
    setForm(config.types[0].emptyForm);
  };

  const switchTab = (key: string) => {
    if (key !== 'add') resetForm();
    setActiveTab(key);
    setSearchTerm('');
    setPageNum(1);
  };

  const selectFormType = (key: string) => {
    if (editing) return;
    setFormType(key);
    setForm(typeConf(key).emptyForm);
  };

  const startEdit = async (row: any) => {
    const conf = typeConf(row.type);
    try {
      const full = await conf.resource.get(row.id);
      setEditing({ id: row.id, type: row.type, full });
      setFormType(row.type);
      setForm(conf.toForm(full));
      setActiveTab('add');
    } catch { notify('Could not load the record.', 'error'); }
  };

  const openPreview = async (row: any) => {
    const conf = typeConf(row.type);
    try {
      const full = await conf.resource.get(row.id);
      setPreview({ full, type: row.type });
    } catch { notify('Could not load the record.', 'error'); }
  };

  // "Open the record" from the Approvals inbox: the entry's own tab (or
  // Deleted), then its document. Which register holds it is told by its
  // record number, since ids repeat across the registers.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const getters = Object.fromEntries(config.types.map((ty) => [ty.key, (n: number) => ty.resource.get(n)]));
    const found = await fetchRecord(id, ref, getters);
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const gone = found.record.status === 'VOID' || found.record.recordStatus === 'VOID';
    switchTab(gone ? 'deleted' : found.kind);
    setPreview({ full: found.record, type: found.kind });
  });

  /**
   * Straight to the printer, without opening the document first.
   *
   * The list row is a light projection, so the full record is fetched before
   * printing — a certificate built from the row alone would go out with half
   * its fields blank.
   */
  const printRow = async (row: any) => {
    const conf = typeConf(row.type);
    if (!conf.printModule || !conf.printDocumentType) {
      notify('No document is configured for this record.', 'error');
      return;
    }
    let full = row;
    try {
      full = await conf.resource.get(row.id);
    } catch {
      notify('Could not load the record.', 'error');
      return;
    }
    await print({
      module: conf.printModule,
      documentType: conf.printDocumentType,
      title: `${conf.recordLabel} ${full?.recordId || row.recordId || ''}`.trim(),
      data: conf.printData ? conf.printData(full) : full
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const conf = typeConf(formType);
    const err = conf.validate?.(form);
    if (err) { notify(err, 'error'); return; }

    const warning = conf.warn?.(form, Boolean(editing));
    if (warning) { setPendingWarning(warning); return; }
    save();
  };

  const save = async () => {
    const conf = typeConf(formType);
    // Form-local fields (`_member` and friends) are for this screen only; the
    // backend declares no columns for them and would drop them silently.
    const payload = stripFormLocal(form);

    setSaving(true);
    try {
      if (editing) {
        // Full-replace, so start from the fetched record and lay the form on top
        // (keeps recordId, status and anything the form does not carry).
        await conf.resource.update(editing.id, { ...editing.full, ...payload });
      } else {
        // No record number — the backend generates it.
        await conf.resource.create({ ...payload, status: 'ACTIVE' });
      }
      // Every register save writes the member's sacrament fields too, and a
      // baptism can add a member outright, so the cached directory behind the
      // pickers is stale the moment this returns.
      invalidateDirectory();
      await refetch();
      resetForm();
      setActiveTab('all');
      notify(editing ? 'Record updated.' : 'Record saved.', 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the record.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmVoid = async (reason: string) => {
    const row = pendingVoid;
    setPendingVoid(null);
    if (!row) return false;
    const conf = typeConf(row.type);
    try {
      const full = await conf.resource.get(row.id);
      await conf.resource.update(row.id, { ...full, status: 'VOID', deleteReason: reason, deletedAt: stamp() });
      invalidateDirectory();
      await refetch();
      notify('Record deleted. Kept under Deleted.', 'success');
      return true;
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the record.', 'error');
      return false;
    }
  };

  const handleRestore = async (row: any) => {
    const conf = typeConf(row.type);
    try {
      const full = await conf.resource.get(row.id);
      await conf.resource.update(row.id, { ...full, status: 'ACTIVE', deleteReason: null, deletedAt: null });
      invalidateDirectory();
      await refetch();
      notify(`${row.record} restored.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not restore the record.', 'error');
    }
  };

  const typeBadge = (row: any) => {
    const ty = typeConf(row.type);
    return <Badge tone={ty.tone}>{ty.shortLabel}</Badge>;
  };

  const recordColumn: Column<any> = { key: 'record', header: 'Record No.', nowrap: true, render: (r) => <strong>{r.record}</strong> };
  const typeColumn: Column<any> = { key: 'type', header: 'Type', nowrap: true, render: typeBadge };
  const actionsColumn: Column<any> = {
    key: 'actions', header: 'Actions', align: 'right', nowrap: true,
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <RowActions
          isDarkMode={isDarkMode}
          items={isDeletedView
            ? [{ icon: '♻️', label: 'Restore', onClick: () => handleRestore(r) }]
            : [
                // Two separate actions: look at it, or send it to the printer.
                // Reading a certificate and issuing one are different jobs.
                { icon: '👁️', label: 'View', onClick: () => openPreview(r) },
                { icon: '🖨️', label: 'Print / Save PDF', onClick: () => printRow(r) },
                { icon: '✏️', label: 'Edit', write: true, onClick: () => startEdit(r) },
                { icon: '🗑️', label: 'Delete', separated: true, danger: true, onClick: () => setPendingVoid(r) }
              ]}
        />
      </span>
    )
  };
  const deletedCols: Array<Column<any>> = [
    { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}</span> },
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> }
  ];
  const columns: Array<Column<any>> = [
    recordColumn, typeColumn, ...config.columns,
    ...(isDeletedView ? deletedCols : []),
    { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' },
    actionsColumn
  ];

  // The certificate takes over the screen so the browser prints it alone.
  if (preview) {
    const conf = typeConf(preview.type);
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '20px' }}>
          <Button variant="secondary" onClick={() => setPreview(null)}>← Back to Register</Button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="ghost" onClick={() => { const p = preview; setPreview(null); startEdit({ id: p.full.id, type: p.type }); }}>✏️ Edit</Button>
            {conf.printModule && conf.printDocumentType && (
              <PrintButton
                module={conf.printModule}
                documentType={conf.printDocumentType}
                data={() => (conf.printData ? conf.printData(preview.full) : preview.full)}
                title={`${conf.recordLabel} ${preview.full?.recordId || ''}`.trim()}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            )}
          </div>
        </div>
        {/* The document itself, in its own frame. Nothing of the application's
            styling reaches it, so it reads the same in dark mode as in light —
            and it is the same file the printer is given. */}
        {!paper && <p style={{ color: t.textMuted }}>Preparing the document…</p>}

        {paper?.error && (
          <div style={{
            padding: '18px 20px', borderRadius: t.radius.lg,
            border: `1px solid ${t.border}`, backgroundColor: t.bgMain, color: t.textMuted
          }}>
            {paper.error}
          </div>
        )}

        {paper?.html && (
          <iframe
            title="Document preview"
            srcDoc={paper.html}
            style={{
              width: '100%', height: '78vh', border: `1px solid ${t.border}`,
              borderRadius: t.radius.md, backgroundColor: '#FFF', display: 'block'
            }}
          />
        )}
      </div>
    );
  }

  const formConf = typeConf(formType);
  const SummaryView = config.summary;

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon={config.icon}
        title={config.title}
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          ...(config.summary ? [{ key: 'summary', label: '📊 Summary' }] : []),
          { key: 'all', label: `All (${counts.all || 0})` },
          ...config.types.map((ty) => ({ key: ty.key, label: `${ty.tabLabel} (${counts[ty.countKey] || 0})` })),
          { key: 'deleted', label: `🗑️ Deleted History (${counts.deleted || 0})`, variant: 'danger' as const },
          { key: 'add', label: editing ? '✏️ Editing…' : '+ Add New Entry' }
        ]}
      />

      {activeTab === 'summary' && SummaryView && <SummaryView isDarkMode={isDarkMode} />}

      {isListView && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm('')}
            placeholder={config.searchPlaceholder}
            actions={(
              <>
                <DateRangeFilter
                  startDate={fromDate} setStartDate={setFromDate} endDate={toDate} setEndDate={setToDate}
                  clear={clearRange} isActive={Boolean(fromDate || toDate)} isDarkMode={isDarkMode}
                />
                {/* The register as a document, alongside the certificate a
                    single row prints from its preview.
                    Only on a single-type tab: the "All" tab mixes baptisms,
                    communions and confirmations, whose columns are nothing
                    alike, and no one template can name all three. */}
                {listPrint && (
                  <PrintButton
                    module={listPrint.module}
                    documentType={listPrint.documentType}
                    size="sm"
                    title={listPrint.label.replace(/\s+/g, '-')}
                    data={() => ({
                      title: listPrint.label,
                      reference: fromDate || toDate
                        ? `${fromDate || 'start'} to ${toDate || 'today'}`
                        : 'All records',
                      rowCount: page.currentItems.length,
                      rows: page.currentItems
                    })}
                    onNotify={notify}
                    isDarkMode={isDarkMode}
                  />
                )}
              </>
            )}
          />

          <TablePanel
            title={config.title}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
            columns={columns}
            rows={page.currentItems}
            loading={loading}
            rowKey={(r) => `${r.type}-${r.id}`}
            onRowClick={(r) => (isDeletedView ? undefined : openPreview(r))}
            emptyMessage="No records found."
          />
        </>
      )}

      {activeTab === 'add' && (
        <FormLayout
          title={editing ? `Edit ${formConf.recordLabel}` : `New ${formConf.recordLabel}`}
          submitLabel={editing ? 'Update Entry' : 'Save to Register'}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={editing ? () => switchTab('all') : undefined}
          maxWidth="860px"
        >
          {/* Type switch — locked while editing an existing record. */}
          <div style={{ display: 'flex', border: `1px solid ${t.primary}`, borderRadius: t.radius.md, overflow: 'hidden', width: 'fit-content', marginBottom: t.spacing.lg }}>
            {config.types.map((ty) => {
              const on = formType === ty.key;
              return (
                <button
                  key={ty.key} type="button"
                  onClick={() => selectFormType(ty.key)}
                  disabled={Boolean(editing)}
                  style={{
                    padding: '10px 20px', border: 'none', cursor: editing ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold', fontSize: t.fontSize.md,
                    backgroundColor: on ? t.primary : t.bgRaised,
                    color: on ? '#FFF' : t.primaryText, opacity: editing && !on ? 0.5 : 1
                  }}
                >
                  {ty.tabLabel}
                </button>
              );
            })}
          </div>

          {formConf.fields(form, setForm, { isDarkMode, isEditing: Boolean(editing) })}
        </FormLayout>
      )}

      <ConfirmDialog
        open={Boolean(pendingWarning)}
        isDarkMode={isDarkMode}
        title="Check this before saving"
        message={pendingWarning}
        confirmLabel="Save anyway"
        cancelLabel="Go back"
        destructive={false}
        onConfirm={() => { setPendingWarning(null); save(); }}
        onCancel={() => setPendingWarning(null)}
      />

      <PromptDialog
        open={Boolean(pendingVoid)}
        title={`Delete ${pendingVoid?.record || ''}`}
        message="Please enter the reason for deleting this register entry. It will be kept under Deleted History."
        placeholder="Reason for deletion"
        confirmLabel="Delete Entry"
        requiredError="A reason is required to remove a register entry."
        onSubmit={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />
    </div>
  );
};

export default MergedRegisterScreen;
